import {
  LocalStorageAnonymousRealUserID,
  LocalStorageAnonymousUserID,
  LocalStorageTokenKey,
} from '@whelk/const/src/token';
import type { UserInfo } from '@whelk/services/src/user';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// const FALLBACK_NAME = '小螺帽';

export interface loginFromInfo {
  phone: string;
  code: string;
}

interface UserState {
  userID: string;
  realUserID: string;
  userName: string;
  webAvatar: string;
  defaultAvatar: string;
  isLogin: boolean;
  showLoginModal: boolean;
  showBindModal: boolean;
  confirmModalOpen: boolean;
  showLogoutModal: boolean;
  loginModalText: {
    leftTitle: string;
    rightTitle: string;
    leftSubTitle: string;
    leftIntro: string;
  };
  formTemporary: loginFromInfo;
  marketActivitySwitchStatus: number;
  bannedAppealModal: {
    show: boolean;
    message: string;
  };
  initUserLoading: boolean;
}

const initialState: UserState = {
  userID: '',
  realUserID: '',
  userName: '',
  webAvatar: '',
  defaultAvatar: '',
  isLogin: false,
  showLoginModal: false,
  showBindModal: false,
  showLogoutModal: false,
  confirmModalOpen: false,
  loginModalText: {
    leftTitle: '扫码登录',
    rightTitle: '手机号登录',
    leftSubTitle: '即可免费使用全部功能',
    leftIntro: '微信扫码登录',
  },
  formTemporary: {
    phone: '',
    code: '',
  },
  marketActivitySwitchStatus: 0,
  bannedAppealModal: {
    show: false,
    message: '',
  },
  initUserLoading: false,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    login(state, action: PayloadAction<UserInfo & { token?: string }>) {
      if (action.payload.token) {
        window.localStorage.setItem(LocalStorageTokenKey, action.payload.token);
      }
      return {
        ...state,
        isLogin: !action.payload.isAnonymous, // 新增匿名登陆模式，这种情况下不算真正的登陆
        userID: action.payload.userID,
        realUserID: action.payload.realUserID,
        userName: (action.payload?.userName || action.payload?.username) ?? '',
        webAvatar: action.payload?.webAvatar?.small,
      };
    },
    logout(state) {
      return {
        ...initialState,
        defaultAvatar: state.defaultAvatar,
        userID: localStorage.getItem(LocalStorageAnonymousUserID) || '',
        realUserID: localStorage.getItem(LocalStorageAnonymousRealUserID) || '',
      };
    },
    // TODO: 把默认头像的逻辑也搬过来
    initAnonymousUser: (state, action: PayloadAction<{ userID: string; realUserID: string }>) => {
      state.userID = action.payload.userID;
      state.realUserID = action.payload.realUserID;
    },
    setShowLoginModal: (state, action: PayloadAction<boolean>) => {
      state.showLoginModal = action.payload;
      if (!action.payload) {
        state.loginModalText = initialState.loginModalText;
      }
    },
    setBannedAppealModal: (state, action: PayloadAction<{ show: boolean; message: string }>) => {
      state.bannedAppealModal = action.payload;
    },
    setShowBindModal: (state, action: PayloadAction<boolean>) => {
      state.showBindModal = action.payload;
    },
    setShowLogoutModal: (state, action: PayloadAction<boolean>) => {
      state.showLogoutModal = action.payload;
    },
    setMarketActivitySwitchStatus: (state, action: PayloadAction<number>) => {
      state.marketActivitySwitchStatus = action.payload;
    },
    setLoginModalText: (state, action: PayloadAction<(typeof initialState)['loginModalText']>) => {
      state.loginModalText = action.payload;
    },
    setDefaultAvatar: (state, action: PayloadAction<string>) => {
      state.defaultAvatar = action.payload;
    },
    setFormTemporary: (state, action: PayloadAction<loginFromInfo>) => {
      state.formTemporary = action.payload;
    },
    setConfirmModalOpen: (state, action: PayloadAction<boolean>) => {
      state.confirmModalOpen = action.payload;
    },
    setInitUserLoading: (state, action: PayloadAction<boolean>) => {
      state.initUserLoading = action.payload;
    },
  },
});

export const {
  setShowLoginModal,
  setBannedAppealModal,
  setShowBindModal,
  setShowLogoutModal,
  login,
  logout,
  setMarketActivitySwitchStatus,
  setLoginModalText,
  setDefaultAvatar,
  initAnonymousUser,
  setFormTemporary,
  setConfirmModalOpen,
  setInitUserLoading,
} = userSlice.actions;

export default userSlice.reducer;
